<script setup lang="ts">
/**
 * PAGE CAGNOTTE — texte mis en forme (HTML de l'éditeur) replié au-delà de
 * `hauteurMax` px, avec « Lire la suite » / « Réduire ». Le bouton n'apparaît
 * QUE si le texte dépasse vraiment : un texte court s'affiche en entier, sans
 * dégradé ni bouton. Sert à la description et à « À quoi serviront les dons »
 * (mêmes règles, Karim 2026-10-12).
 */
import { useSanitizeHtml } from "~/composables/useSanitizeHtml";

const props = withDefaults(defineProps<{ html: string; hauteurMax?: number }>(), { hauteurMax: 420 });

const { sanitize } = useSanitizeHtml();
const contenu = ref<HTMLElement | null>(null);
const deplie = ref(false);
const depasse = ref(false);

function mesurer() {
  if (contenu.value) depasse.value = contenu.value.scrollHeight > props.hauteurMax + 24;
}
let observateur: ResizeObserver | null = null;
onMounted(() => {
  mesurer();
  if (contenu.value) {
    observateur = new ResizeObserver(mesurer);
    observateur.observe(contenu.value);
  }
});
watch(() => props.html, () => nextTick(mesurer));
onBeforeUnmount(() => observateur?.disconnect());
</script>

<template>
  <div>
    <div class="relative">
      <!-- eslint-disable vue/no-v-html -->
      <!-- Reason: contenu sanitizé via sanitize() (DOMPurify), comme la page actuelle -->
      <div
        ref="contenu"
        class="prose prose-gray max-w-none text-[16px] leading-relaxed overflow-hidden"
        :style="!deplie && depasse ? { maxHeight: `${hauteurMax}px` } : undefined"
        v-html="sanitize(html)"
      />
      <!-- eslint-enable vue/no-v-html -->
      <div
        v-if="depasse && !deplie"
        class="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-white to-transparent"
      />
    </div>
    <button
      v-if="depasse"
      type="button"
      class="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-200 text-sm text-(--color-ink) hover:border-(--color-main2) transition-colors"
      style="font-weight: 600 !important"
      @click="deplie = !deplie"
    >
      {{ deplie ? $t("campagne.pageCagnotte.reduire") : $t("campagne.pageCagnotte.lireLaSuite") }}
      <UIcon :name="deplie ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="size-4" />
    </button>
  </div>
</template>
